import { useState, useEffect } from 'react'
import { cleanMonitorUrl, rememberMonitorUrl, takePendingMonitorUrl } from '@/lib/pending-monitor'
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useForm, useStore } from '@tanstack/react-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { StatusBadge } from '@/components/status-badge'
import { WebVitals } from '@/components/web-vitals'
import { PreviewScreenshot } from '@/components/preview-screenshot'
import { apiV2, ApiV2Error } from '@/lib/api-v2-client'
import { useAuth } from '@/hooks/use-auth'
import { useUpgradePrompt } from '@/components/upgrade-prompt'
import { WorkEmailDialog } from '@/components/work-email-dialog'
import { BlockedActionButton, CreateBlockReason } from '@/components/create-monitor-block'
import { createMonitorBlock } from '@/lib/free-domain'
import { categorizeApiError, showsToast, type ApiErrorInfo } from '@/lib/monitor-errors'
import { useFreePlan } from '@/hooks/use-free-plan'
import { FREE_PLAN, FREE_REGION_CODE, PRO_ADDS, isFreeMonitorType } from '@/lib/free-plan'
import { isFreeTier } from '@/lib/subscription-access'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { AreaList } from '@/components/area-list'
import { ALL_AREAS, edgeAreaSettings, edgeSupports, hasEnoughAreas, MIN_AREAS } from '@/lib/edge-areas'
import { createMonitorSchema, buildBrowserSettings, browserMinCadence, checkIntervalOptions, defaultCheckInterval, fastSlotLimit as fastSlotLimitFor, hasRegionChoice, timeoutMax, BROWSER_INTERVAL_OPTIONS, BROWSER_SCRIPT_TEMPLATES, getFieldError, HTTP_METHODS, DNS_RECORD_TYPES, DNS_PLACEHOLDER_HINTS, DNS_VALUE_VALIDATORS, DNS_RECORD_CHANGE_TYPES, parsePortsSpec, parseRecordList, type HttpMethod, type MonitorType, type BrowserEngine } from '@/lib/forms'
import { toast } from 'sonner'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'
import { ThirdPartyCreateFlow } from '@/components/third-party/create-flow'
import { useThirdPartyCatalog } from '@/hooks/use-third-party-catalog'

interface Assertion {
  type: 'json_path' | 'regex' | 'header' | 'status_code' | 'contains' | 'not_contains'
  target: string
  operator: string
  value: string
}

const ASSERTION_TYPES = [
  { value: 'json_path', label: 'JSONPath' },
  { value: 'contains', label: 'Body contains' },
  { value: 'not_contains', label: 'Body not contains' },
  { value: 'regex', label: 'Regex match' },
  { value: 'header', label: 'Response header' },
  { value: 'status_code', label: 'Status code' },
] as const

const OPERATORS = [
  { value: 'eq', label: '=' },
  { value: 'neq', label: '!=' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'not contains' },
  { value: 'gt', label: '>' },
  { value: 'lt', label: '<' },
  { value: 'gte', label: '>=' },
  { value: 'lte', label: '<=' },
] as const

const PRESELECTABLE_TYPES = ['http', 'dns', 'ssl', 'heartbeat', 'browser', 'third_party'] as const

// The monitor type picker. Free accounts get `http` and `keyword`; the rest read "— Pro".
const MONITOR_TYPE_OPTIONS: ReadonlyArray<{ value: MonitorType; label: string; group: 'synthetic' | 'uptime' | 'third_party' }> = [
  { value: 'browser', label: 'Browser (Playwright)', group: 'synthetic' },
  { value: 'http', label: 'HTTP(S)', group: 'uptime' },
  { value: 'keyword', label: 'Keyword (HTTP + text match)', group: 'uptime' },
  { value: 'tcp', label: 'TCP / Port', group: 'uptime' },
  { value: 'ping', label: 'Ping (ICMP)', group: 'uptime' },
  { value: 'dns', label: 'DNS', group: 'uptime' },
  { value: 'ssl', label: 'SSL Certificate', group: 'uptime' },
  { value: 'domain', label: 'Domain expiry', group: 'uptime' },
  { value: 'port_scan', label: 'Port scan', group: 'uptime' },
  { value: 'dns_record', label: 'DNS record change', group: 'uptime' },
  { value: 'blocklist', label: 'DNS blocklist', group: 'uptime' },
  { value: 'sitemap', label: 'Sitemap health', group: 'uptime' },
  { value: 'heartbeat', label: 'Heartbeat / Cron', group: 'uptime' },
  // Offered only while the catalog answers (OpenSpec 128): with the feature off the type is hidden.
  { value: 'third_party', label: 'Third party service', group: 'third_party' },
]

export const Route = createFileRoute('/_authenticated/dashboard/monitors/new')({
  // `?type=` pre-selects the monitor type so each split-nav section's "+ New"
  // lands on the right default (Browser Checks → browser; Uptime → http).
  validateSearch: (s: Record<string, unknown>): { type?: MonitorType; url?: string; service?: string } => {
    const t =
      typeof s.type === 'string' && (PRESELECTABLE_TYPES as readonly string[]).includes(s.type)
        ? (s.type as MonitorType)
        : undefined
    // From the website checker's "Monitor this site" (see pending-monitor.ts).
    // `?service=cloudflare` preselects a service in the third party picker (a catalog slug).
    const service = typeof s.service === 'string' && /^[a-z0-9][a-z0-9-]{0,63}$/.test(s.service) ? s.service : undefined
    return { type: t, url: cleanMonitorUrl(s.url), service }
  },
  component: NewMonitorPage,
})

function parseHeadersString(str: string): Record<string, string> {
  const headers: Record<string, string> = {}
  if (!str.trim()) return headers
  for (const line of str.split('\n')) {
    const idx = line.indexOf(':')
    if (idx > 0) {
      headers[line.slice(0, idx).trim()] = line.slice(idx + 1).trim()
    }
  }
  return headers
}

function formatInterval(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${seconds / 60}m`
  return `${seconds / 3600}h`
}

function isLocalOrPrivateUrl(url: string): boolean {
  if (!url) return false
  const trimmed = url.trim().toLowerCase()
  try {
    const u = new URL(trimmed.includes('://') ? trimmed : `https://${trimmed}`)
    const host = u.hostname
    if (host === 'localhost' || host === '127.0.0.1' || host === '::1') return true
    if (host.endsWith('.local') || host.endsWith('.internal')) return true
    // 10.0.0.0/8, 172.16.0.0/12, 192.168.0.0/16
    const m = host.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/)
    if (m) {
      const [, a, b] = m.map(Number)
      if (a === 10) return true
      if (a === 172 && b >= 16 && b <= 31) return true
      if (a === 192 && b === 168) return true
    }
    return false
  } catch {
    return false
  }
}

function NewMonitorPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { type: preselectType, url: presetUrl, service: presetService } = Route.useSearch()
  // A site picked in the website checker: from the link, or remembered across
  // signup and checkout. Read once.
  const [initialUrl] = useState(() => presetUrl ?? takePendingMonitorUrl() ?? '')
  const { organization, user } = useAuth()
  // The free plan: one fixed location, 30-second checks, HTTP and keyword monitors only.
  const freeTier = isFreeTier(organization)
  // The banner on top of the page says why. The form stays locked, so nobody fills it in for nothing.
  const createBlock = createMonitorBlock(organization, user)
  // Keep a site picked in the website checker for when the user comes back from Settings.
  useEffect(() => {
    if (!initialUrl) return
    if (createBlock) rememberMonitorUrl(initialUrl)
    else takePendingMonitorUrl()
  }, [createBlock, initialUrl])
  const freePlan = useFreePlan()
  const { show: showUpgradePrompt } = useUpgradePrompt()
  // The third party type shows only while the catalog answers (the feature switch is on).
  const thirdPartyCatalog = useThirdPartyCatalog()
  const thirdPartyOffered = thirdPartyCatalog.isSuccess && thirdPartyCatalog.data.data.categories.length > 0
  const [aiLoading, setAiLoading] = useState(false)
  // Type-essential fields are always visible (moved out of the collapsible);
  // only the cross-cutting "More options" (timeouts, alert tuning) collapse,
  // collapsed by default to keep the form short.
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [nameManuallyEdited, setNameManuallyEdited] = useState(false)

  const { data: regionsData } = useQuery({
    queryKey: ['regions'],
    queryFn: () => apiV2.getRegions(),
  })
  // `eu-free` is the free plan's own location: paid plans pick from the other regions.
  const regions = (regionsData?.data ?? []).filter((r) => r.code !== FREE_REGION_CODE)
  const [selectedRegions, setSelectedRegions] = useState<number[]>([])
  // Extra check areas (change 121). Empty until the customer ticks one; the picker hides itself on plans without them.
  const [edgeAreas, setEdgeAreas] = useState<string[]>([])
  const { data: edgeInfoData } = useQuery({
    queryKey: ['edge-areas'],
    queryFn: () => apiV2.getEdgeAreas(),
    staleTime: 5 * 60_000,
    enabled: !freeTier,
  })
  const edgeInfo = edgeInfoData?.data
  // With the area catalog, one list of areas replaces the plain region list.
  const unifiedAreas = !freeTier && !!edgeInfo && edgeInfo.cap > 0
  const [regionsInitialized, setRegionsInitialized] = useState(false)

  // Auto-select all regions on first load
  if (regions.length > 0 && !regionsInitialized) {
    setSelectedRegions(regions.map((r) => r.id))
    setRegionsInitialized(true)
  }

  // Team and Business include every area, so the form starts with all that run. Pro picks its own.
  const [areasPreset, setAreasPreset] = useState(false)
  if (!areasPreset && edgeInfo && regionsInitialized && edgeInfo.cap >= ALL_AREAS) {
    setEdgeAreas(edgeInfo.areas.filter((a) => a.live).map((a) => a.code))
    setAreasPreset(true)
  }

  const [apiError, setApiError] = useState<ApiErrorInfo | null>(null)
  const [workEmailOpen, setWorkEmailOpen] = useState(false)
  const [autoSwitchedNotice, setAutoSwitchedNotice] = useState<string | null>(null)

  // "Test now" — run a one-off browser preview (screenshot + Web Vitals)
  // before the monitor is saved. Does NOT create anything.
  const previewMutation = useMutation({
    mutationFn: ({ url, script }: { url: string; script?: string }) =>
      apiV2.browserPreview(url, script),
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Preview failed'),
  })

  // "Test now" for HTTP(S) monitors — a server-side check that mirrors the
  // monitor's own settings (method, headers, verify_ssl) so the Test verdict
  // matches what the saved monitor will report (not a bare GET that ignores
  // auth + SSL and calls any <500 "up").
  const httpTestMutation = useMutation({
    mutationFn: (opts: { url: string } & import('@/lib/api-v2-client').WebsiteCheckOptions) =>
      apiV2.checkWebsite(opts.url, opts),
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Test failed'),
  })

  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof apiV2.createMonitor>[0]) => apiV2.createMonitor(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['monitors'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
      setApiError(null)
      toast.success('Monitor created successfully')
      // Go to the monitor's detail page (which polls its checks/incidents) so the
      // user sees the FIRST check result instead of a silent "pending" list row.
      const id = result?.data?.id
      if (id) {
        navigate({ to: '/dashboard/monitors/$id', params: { id } })
      } else {
        navigate({ to: '/dashboard/monitors' })
      }
    },
    onError: (error) => {
      const info = categorizeApiError(error instanceof Error ? error : new Error(String(error)), { freeTier })
      // A plan-gate now returns 402; show the upgrade MODAL and suppress the inline
      // upgrade hint so the user doesn't get a double prompt (the modal owns it).
      // Field-specific errors keep their inline message, and so does an unconfirmed
      // email: that is not a plan limit, so it never opens the upgrade modal.
      if (error instanceof ApiV2Error && error.status === 402 && !info.field && info.kind !== 'email') {
        setApiError(null)
        showUpgradePrompt(error.message)
        return
      }
      // No work email on a free plan: one clear choice, so it opens as a dialog, not a box under the form.
      if (info.kind === 'domain') {
        setApiError(null)
        setWorkEmailOpen(true)
        return
      }
      setApiError(info)
      // A toast disappears in seconds, so only an unknown error is one. The rest stay in the page.
      if (showsToast(info)) {
        toast.error(info.message)
      }
    },
  })

  const form = useForm({
    defaultValues: {
      name: '',
      url: initialUrl,
      // `?type=browser` from the Browser Checks nav is a Pro type: a free account starts on HTTP.
      monitor_type: (freeTier && preselectType && !isFreeMonitorType(preselectType) ? 'http' : (preselectType ?? 'http')) as MonitorType,
      http_method: 'GET' as HttpMethod,
      request_headers: '',
      request_body: '',
      verify_ssl: true,
      dns_record_type: 'A',
      dns_expected_value: '',
      dns_server: '',
      ssl_expiry_threshold: 30,
      domain_warn_days: 30,
      port_scan_ports: '',
      dns_record_expected: '',
      blocklist_zones: '',
      sitemap_sample: 20,
      keyword: '',
      browser_script: '',
      browser_engine: 'chromium' as BrowserEngine,
      visual_regression: false,
      visual_threshold: 0.97,
      check_interval: freeTier ? FREE_PLAN.intervalSeconds : defaultCheckInterval(organization?.plan),
      timeout: 30,
      confirmation_threshold: 2,
      response_time_threshold: 0,
      escalation_policy_id: '',
      reminder_interval_minutes: null as number | null,
    },
    validators: {
      onSubmit: createMonitorSchema,
    },
    onSubmit: async ({ value }) => {
      // An empty selection would reach the API as "no region_ids" = every region.
      if (!freeTier && unifiedAreas && edgeInfo && !hasEnoughAreas(value.monitor_type, edgeInfo.areas, { regionIds: selectedRegions, edgeAreas })) {
        setApiError({ field: 'regions', message: `Pick at least ${MIN_AREAS} check areas.` })
        return
      }
      if (!freeTier && !unifiedAreas && !hasRegionChoice(selectedRegions, regions.length)) {
        setApiError({ field: 'regions', message: 'Select at least one check region.' })
        return
      }
      setApiError(null)
      const settings: Record<string, unknown> = {}

      // A keyword monitor is an HTTP check plus a text match: it takes the same settings.
      if (value.monitor_type === 'http' || value.monitor_type === 'keyword') {
        settings.method = value.http_method
        const headers = parseHeadersString(value.request_headers)
        if (Object.keys(headers).length > 0) settings.headers = headers
        if (value.request_body.trim()) settings.body = value.request_body
        // Persist the SSL-verification choice the probe now honors. Default true;
        // false lets self-signed / internal HTTPS endpoints be monitored at all.
        settings.verify_ssl = value.verify_ssl
      } else if (value.monitor_type === 'dns') {
        settings.dns_record_type = value.dns_record_type
        if (value.dns_expected_value.trim()) settings.dns_expected_value = value.dns_expected_value
        if (value.dns_server.trim()) settings.dns_server = value.dns_server
      } else if (value.monitor_type === 'ssl') {
        settings.expiry_threshold = value.ssl_expiry_threshold
      } else if (value.monitor_type === 'domain') {
        settings.warn_days = value.domain_warn_days
      } else if (value.monitor_type === 'port_scan') {
        settings.ports = parsePortsSpec(value.port_scan_ports)
      } else if (value.monitor_type === 'dns_record') {
        settings.record_type = value.dns_record_type
        settings.expected = parseRecordList(value.dns_record_expected)
      } else if (value.monitor_type === 'blocklist') {
        const zones = parseRecordList(value.blocklist_zones)
        if (zones.length > 0) settings.zones = zones
      } else if (value.monitor_type === 'sitemap') {
        settings.sample = value.sitemap_sample
      } else if (value.monitor_type === 'browser') {
        Object.assign(settings, buildBrowserSettings(value))
      }

      if (value.response_time_threshold > 0) {
        settings.response_time_threshold = value.response_time_threshold
      }

      if (value.monitor_type === 'keyword' && value.keyword.trim()) settings.keyword = value.keyword.trim()

      if (assertions.length > 0) {
        settings.assertions = assertions.filter((a) => a.target || a.value)
      }

      if (!freeTier) Object.assign(settings, edgeAreaSettings(value.monitor_type, edgeAreas, false))

      // Clamp browser interval up to the plan floor so an untouched default
      // (the field may still hold a sub-floor value) never 422s on submit.
      // Free monitors send the chosen interval too (it starts at the free plan's 30 s).
      const interval =
        value.monitor_type === 'browser'
          ? Math.max(value.check_interval, browserMinCadence(organization?.plan))
          : value.check_interval

      createMutation.mutate({
        name: value.name.trim(),
        url: value.url.trim(),
        interval,
        timeout: value.timeout,
        confirmation_threshold: value.confirmation_threshold,
        reminder_interval_minutes: value.reminder_interval_minutes,
        settings,
        ...(value.monitor_type !== 'http' && { monitor_type: value.monitor_type }),
        // A free monitor has no region choice: the server places it at the free location.
        // An empty list with edge areas is a monitor with no core probe; all of them is the default.
        ...(!freeTier &&
          selectedRegions.length < regions.length &&
          (selectedRegions.length > 0 || (unifiedAreas && edgeSupports(value.monitor_type) && edgeAreas.length > 0)) && {
            region_ids: selectedRegions,
          }),
      })
    },
  })

  // Response assertions (dynamic list, managed outside TanStack Form)
  const [assertions, setAssertions] = useState<Assertion[]>([])

  // Smart defaults: debounce URL input, fetch suggestions
  const [liveUrl, setLiveUrl] = useState('')
  const debouncedUrl = useDebouncedValue(liveUrl, 500)

  const { data: smartDefaults } = useQuery({
    queryKey: ['smart-defaults', debouncedUrl],
    queryFn: () => apiV2.getSmartDefaults(debouncedUrl),
    enabled: debouncedUrl.length > 3 && debouncedUrl.includes('.'),
    retry: false,
    staleTime: 60_000,
  })

  // The expected status codes the SAVED monitor will judge against — so "Test now"
  // agrees with the monitor (a 4xx shows Down under default codes, not a false "Up").
  // Precedence mirrors the monitor: an explicit `status_code eq` assertion wins (e.g. a
  // monitor that expects 403), else the smart-default list, else the HTTP default.
  const effectiveExpectedCodes = (): number[] => {
    const sc = assertions.find((a) => a.type === 'status_code' && a.operator === 'eq')
    if (sc) {
      const n = Number(sc.value)
      if (Number.isInteger(n) && n >= 100 && n <= 599) return [n]
    }
    const raw = (smartDefaults?.data?.settings as Record<string, unknown> | undefined)?.expected_status_codes
    if (Array.isArray(raw)) {
      const nums = raw.map(Number).filter((n) => Number.isInteger(n))
      if (nums.length > 0) return nums
    }
    return [200, 201, 204, 301, 302]
  }

  // Auto-fill name from smart defaults (only if user hasn't manually edited)
  useEffect(() => {
    const name = smartDefaults?.data?.name
    if (name && !nameManuallyEdited && !name.includes('://')) {
      form.setFieldValue('name', name)
    }
  }, [smartDefaults?.data?.name, nameManuallyEdited])

  // One-shot prefill from the public /tools/browser-check tool, handed off through
  // signup via localStorage (see tools/browser-check.tsx). Applied after mount (not
  // in a useState initializer) so SSR and the first client render match, then cleared
  // so a later direct visit to this form isn't pre-filled. Seeding liveUrl drives the
  // smart-defaults query → name auto-fill, exactly as if the user had typed the URL.
  useEffect(() => {
    if (typeof window === 'undefined') return
    let raw: string | null = null
    try {
      raw = window.localStorage.getItem('uptrack_prefill')
      if (raw) window.localStorage.removeItem('uptrack_prefill')
    } catch {
      return
    }
    if (!raw) return
    try {
      const p = JSON.parse(raw) as { url?: string; type?: MonitorType }
      if (p.type) form.setFieldValue('monitor_type', p.type)
      if (p.url) {
        form.setFieldValue('url', p.url)
        // Browser storage is an outside system, read once on mount. It cannot run in the server render.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setLiveUrl(p.url)
      }
    } catch {
      /* malformed handoff — ignore */
    }
  }, [])

  const isPending = createMutation.isPending
  const defaults = smartDefaults?.data

  // Warn when the URL matches an existing monitor (dupes are a real footgun —
  // users accidentally create the same monitor multiple times)
  const { data: existingMonitors } = useQuery({
    queryKey: ['monitors'],
    queryFn: () => apiV2.listMonitors({ per_page: 100 }),
    staleTime: 30_000,
  })
  // Free plan: how many of the monitors are used. The server enforces the limit; this only informs.
  // The limit counts ACTIVE monitors: paused ones (an old free account can hold many) do not use it.
  const { data: monitorStats } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => apiV2.getDashboardStats(),
    enabled: freeTier,
  })
  const activeInList = existingMonitors?.data?.filter((m) => (m.status ?? 'active') === 'active').length
  const usedMonitors = monitorStats?.data?.active_monitors ?? activeInList ?? 0
  const atFreeLimit = freeTier && usedMonitors >= freePlan.monitors
  const duplicateMonitor = (() => {
    if (!debouncedUrl || !existingMonitors?.data) return null
    const normalize = (s: string) => s.trim().replace(/\/+$/, '').toLowerCase()
    const target = normalize(debouncedUrl)
    if (!target) return null
    return existingMonitors.data.find((m) => normalize(m.url ?? '') === target) ?? null
  })()

  // Auto-switch 30s → 60s when the user's 30s slot limit is reached.
  // No subscription yet: 10 slots. Paid plans: unlimited (so no cap).
  const fastSlotLimit = fastSlotLimitFor(organization?.plan, freeTier)
  const usedFastSlots = existingMonitors?.data?.filter((m) => m.interval === 30).length ?? 0
  const fastSlotsExhausted = usedFastSlots >= fastSlotLimit

  useEffect(() => {
    const sub = form.store.subscribe(() => {
      const val = form.state.values.check_interval
      if (val === 30 && fastSlotsExhausted) {
        form.setFieldValue('check_interval', 60)
        setAutoSwitchedNotice(
          `You've used all ${fastSlotLimit} of your 30-second slots — switched this monitor to 1 minute. You can still use all 30-second slots on your existing monitors.`,
        )
      }
    })
    return () => sub.unsubscribe()
  }, [fastSlotsExhausted, fastSlotLimit])

  const citiesAllowed = useStore(form.store, (state) => edgeSupports(state.values.monitor_type))
  const isThirdParty = useStore(form.store, (state) => state.values.monitor_type === 'third_party')

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-3xl font-semibold">New Monitor</h1>
        <p className="text-muted-foreground">
          {isThirdParty
            ? 'Pick a service you depend on and get alerted when its status page reports a problem'
            : "Enter a URL and we'll set up monitoring with smart defaults"}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-xl">What do you want to monitor?</CardTitle>
          <CardDescription className="text-base">
            {isThirdParty ? 'Choose the service, then what to watch' : "Just paste a URL — we'll handle the rest"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {freeTier && (
            <div className="mb-4 rounded-md border border-border bg-muted px-4 py-3 text-sm" data-testid="free-plan-summary">
              <p className="font-medium text-foreground">
                Free plan: checked every {freePlan.intervalSeconds} seconds from {freePlan.locationLabel}
              </p>
              <p className="mt-1 text-body">
                <span className="num">{usedMonitors}</span> of <span className="num">{freePlan.monitors}</span> free monitors used.
                {atFreeLimit
                  ? ` You are at the limit. Delete a monitor, or move to Pro for ${PRO_ADDS}.`
                  : ` Pro adds ${PRO_ADDS}.`}{' '}
                <Link to="/pricing" hash="plan-pro" className="whitespace-nowrap text-primary hover:underline">
                  See Pro
                </Link>
              </p>
            </div>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              if (createBlock) return
              form.handleSubmit()
            }}
            className="space-y-4"
          >
            {/* Primary: Monitor Type */}
            <form.Field name="monitor_type">
              {(field) => (
                <div className="space-y-2">
                  <Label>Monitor Type</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) => field.handleChange(v as MonitorType)}
                    disabled={isPending || !!createBlock}
                  >
                    <SelectTrigger aria-label="Monitor Type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Synthetic checks</SelectLabel>
                        {MONITOR_TYPE_OPTIONS.filter((o) => o.group === 'synthetic').map((o) => (
                          <SelectItem key={o.value} value={o.value} disabled={freeTier && !isFreeMonitorType(o.value)}>
                            {o.label}{freeTier && !isFreeMonitorType(o.value) ? ' — Pro' : ''}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel>Uptime monitors</SelectLabel>
                        {MONITOR_TYPE_OPTIONS.filter((o) => o.group === 'uptime').map((o) => (
                          <SelectItem key={o.value} value={o.value} disabled={freeTier && !isFreeMonitorType(o.value)}>
                            {o.label}{freeTier && !isFreeMonitorType(o.value) ? ' — Pro' : ''}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                      {(thirdPartyOffered || field.state.value === 'third_party') && (
                        <SelectGroup>
                          <SelectLabel>Services you depend on</SelectLabel>
                          {MONITOR_TYPE_OPTIONS.filter((o) => o.group === 'third_party').map((o) => (
                            <SelectItem key={o.value} value={o.value} disabled={freeTier && !isFreeMonitorType(o.value)}>
                              {o.label}{freeTier && !isFreeMonitorType(o.value) ? ' — Pro' : ''}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      )}
                    </SelectContent>
                  </Select>
                  {freeTier && (
                    <p className="text-sm text-muted-foreground">
                      The free plan has HTTP and keyword monitors. Ping, port, DNS, SSL, domain, heartbeat and browser checks are on Pro.
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            {/* A third party monitor has its own flow (below the form): no URL, no probe settings. */}
            {!isThirdParty && (<>
            <fieldset disabled={!!createBlock} className="min-w-0 space-y-4">
            {/* Primary: URL — the hero input */}
            <form.Subscribe selector={(state) => state.values.monitor_type}>
              {(monitorType) => (
                <>
                <form.Field name="url">
                  {(field) => (
                    <div className="space-y-2">
                      <Label htmlFor="url">
                        {monitorType === 'heartbeat'
                          ? 'Name (e.g. nightly-backup)'
                          : monitorType === 'tcp'
                            ? 'Host and port'
                            : monitorType === 'ping'
                              ? 'Host'
                              : monitorType === 'http' || monitorType === 'browser' || monitorType === 'keyword'
                                ? 'URL'
                                : 'Domain'}
                      </Label>
                      {monitorType === 'heartbeat' ? (
                        <Input
                          id="url"
                          className="h-12 text-lg"
                          placeholder="nightly-backup-job"
                          value={field.state.value}
                          onChange={(e) => {
                            field.handleChange(e.target.value)
                            setLiveUrl(e.target.value)
                          }}
                          onBlur={field.handleBlur}
                          disabled={isPending}
                          autoFocus
                        />
                      ) : (
                      <Input
                        id="url"
                        className="h-12 text-lg"
                        placeholder={
                          monitorType === 'tcp'
                            ? 'db.example.com:5432'
                            : monitorType === 'http' || monitorType === 'browser' || monitorType === 'keyword'
                              ? 'https://api.example.com/health'
                              : monitorType === 'sitemap'
                                ? 'https://example.com/sitemap.xml'
                                : 'example.com'
                        }
                        value={field.state.value}
                        onChange={(e) => {
                          field.handleChange(e.target.value)
                          setLiveUrl(e.target.value)
                        }}
                        onBlur={field.handleBlur}
                        disabled={isPending}
                        autoFocus
                      />
                      )}
                      {monitorType === 'heartbeat' && (
                        <p className="text-xs text-muted-foreground">
                          After creating, you'll get a unique ping URL. Your cron job or service pings this URL on each run.
                        </p>
                      )}
                      {monitorType === 'tcp' && (
                        <p className="text-xs text-muted-foreground">
                          Host and port to connect to, e.g. <code className="font-mono">db.example.com:5432</code>. The monitor is up when the TCP connection succeeds.
                        </p>
                      )}
                      {monitorType === 'ping' && (
                        <p className="text-xs text-muted-foreground">
                          Hostname or IP, e.g. <code className="font-mono">example.com</code>. The monitor is up when the host replies to an ICMP echo (ping).
                        </p>
                      )}
                      {monitorType === 'keyword' && (
                        <p className="text-xs text-muted-foreground">
                          The page is fetched over HTTP; it&rsquo;s down if the response doesn&rsquo;t contain your keyword below.
                        </p>
                      )}
                      {(monitorType === 'http' || monitorType === 'browser') && !field.state.value && (
                        <p className="text-xs text-muted-foreground">
                          Or try an example:{' '}
                          {['https://wikipedia.org', 'https://playwright.dev'].map((ex) => (
                            <button
                              key={ex}
                              type="button"
                              className="mr-1 rounded bg-secondary px-2 py-0.5 font-mono text-xs hover:bg-accent"
                              onClick={() => {
                                field.handleChange(ex)
                                setLiveUrl(ex)
                              }}
                            >
                              {ex.replace('https://', '')}
                            </button>
                          ))}
                        </p>
                      )}
                      {field.state.meta.errors?.[0] && (
                        <p className="text-sm text-destructive">
                          {typeof field.state.meta.errors[0] === 'string'
                            ? field.state.meta.errors[0]
                            : getFieldError(field.state.meta.errors[0])}
                        </p>
                      )}
                      {duplicateMonitor && monitorType !== 'heartbeat' && (
                        <p className="text-sm text-status-degraded">
                          A monitor for this URL already exists:{' '}
                          <Link
                            to="/dashboard/monitors/$id"
                            params={{ id: duplicateMonitor.id }}
                            className="font-medium underline"
                          >
                            {duplicateMonitor.name}
                          </Link>
                          . You can still create another, but duplicates may produce duplicate alerts.
                        </p>
                      )}
                      {(monitorType === 'http' || monitorType === 'browser') &&
                        !!field.state.value &&
                        !isLocalOrPrivateUrl(field.state.value) &&
                        (monitorType === 'browser' ? (
                          <div className="space-y-2 pt-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={previewMutation.isPending}
                              onClick={() =>
                                previewMutation.mutate({
                                  url: field.state.value,
                                  script: form.state.values.browser_script,
                                })
                              }
                            >
                              {previewMutation.isPending ? 'Running your script…' : 'Test now'}
                            </Button>
                            <p className="text-xs text-muted-foreground">
                              Runs your script steps and assertions in a real Chrome and screenshots the
                              result — the same way the saved monitor will. Leave the script empty to just
                              load the page.
                            </p>
                            {previewMutation.isPending && (
                              <p className="text-xs text-muted-foreground">
                                Running in a real browser — this takes ~10–20s.
                              </p>
                            )}
                            {previewMutation.data && (
                              <div className="space-y-3 rounded-lg border border-border p-3">
                                <div className="flex items-center gap-2 text-sm">
                                  <StatusBadge status={previewMutation.data.status === 'up' ? 'up' : 'down'} />
                                  <span className="text-muted-foreground">
                                    {previewMutation.data.duration_ms.toLocaleString()} ms
                                  </span>
                                </div>
                                {previewMutation.data.screenshot_url && (
                                  <PreviewScreenshot url={previewMutation.data.screenshot_url} />
                                )}
                                {previewMutation.data.web_vitals && (
                                  <WebVitals vitals={previewMutation.data.web_vitals} />
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-2 pt-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={httpTestMutation.isPending}
                              onClick={() => httpTestMutation.mutate({
                                url: field.state.value,
                                // Mirror the monitor's own config so Test agrees with it.
                                method: form.state.values.http_method,
                                headers: parseHeadersString(form.state.values.request_headers),
                                verify_ssl: form.state.values.verify_ssl,
                                expected_status_codes: effectiveExpectedCodes(),
                              })}
                            >
                              {httpTestMutation.isPending ? 'Testing…' : 'Test now'}
                            </Button>
                            {httpTestMutation.data && (
                              <div className="space-y-2 rounded-lg border border-border p-3">
                                {httpTestMutation.data.data.results.map((r, i) => (
                                  <div key={i} className="flex items-center gap-2 text-sm">
                                    <StatusBadge
                                      status={r.status === 'up' || r.status === 'down' ? r.status : 'unknown'}
                                      label={r.status === 'up' || r.status === 'down' ? undefined : 'Unavailable'}
                                    />
                                    <span className="text-muted-foreground">
                                      {r.location ? `${r.location} · ` : ''}
                                      {r.status_code ? `HTTP ${r.status_code} · ` : ''}
                                      {r.response_time != null ? `${r.response_time.toLocaleString()} ms` : ''}
                                      {r.tls_verified === false ? ' · TLS verification failed' : ''}
                                      {r.error ? ` · ${r.error}` : ''}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      {monitorType !== 'heartbeat' && liveUrl && isLocalOrPrivateUrl(liveUrl) && (
                        <div className="rounded-md border border-status-degraded bg-status-degraded-soft px-3 py-2 text-sm text-status-degraded">
                          <p className="font-medium">This looks like a private or localhost URL.</p>
                          <p className="text-xs mt-1 text-status-degraded">
                            Uptrack monitors public URLs only. For internal services, expose them via Tailscale Funnel or Cloudflare Tunnel first.
                          </p>
                        </div>
                      )}
                      {apiError?.field === 'url' && (
                        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm">
                          <p className="font-medium text-destructive">{apiError.message}</p>
                          {apiError.hint && (
                            <p className="text-xs mt-1 text-muted-foreground">{apiError.hint}</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </form.Field>
                {monitorType === 'keyword' && (
                  <form.Field name="keyword">
                    {(field) => (
                      <div className="space-y-2">
                        <Label htmlFor="keyword">Keyword to find</Label>
                        <Input
                          id="keyword"
                          placeholder="Welcome"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          disabled={isPending}
                        />
                        {field.state.meta.errors?.[0] && (
                          <p className="text-sm text-destructive">{getFieldError(field.state.meta.errors[0])}</p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          The check fails if the response body doesn&rsquo;t contain this text (case-sensitive).
                        </p>
                      </div>
                    )}
                  </form.Field>
                )}
                {monitorType === 'port_scan' && (
                  <form.Field name="port_scan_ports">
                    {(field) => (
                      <div className="space-y-2">
                        <Label htmlFor="port_scan_ports">Ports</Label>
                        <Input
                          id="port_scan_ports"
                          placeholder="443, 80, 22:closed"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          disabled={isPending}
                        />
                        <p className="text-xs text-muted-foreground">
                          Comma-separated ports. Each is expected <code className="font-mono">open</code> unless you add{' '}
                          <code className="font-mono">:closed</code> (e.g. <code className="font-mono">22:closed</code>). Down if any port&rsquo;s state differs.
                        </p>
                      </div>
                    )}
                  </form.Field>
                )}
                {monitorType === 'dns_record' && (
                  <div className="grid gap-4 sm:grid-cols-[8rem_1fr]">
                    <form.Field name="dns_record_type">
                      {(field) => (
                        <div className="space-y-2">
                          <Label>Record type</Label>
                          <Select value={field.state.value} onValueChange={field.handleChange} disabled={isPending}>
                            <SelectTrigger aria-label="Record type">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {DNS_RECORD_CHANGE_TYPES.map((t) => (
                                <SelectItem key={t} value={t}>{t}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </form.Field>
                    <form.Field
                      name="dns_record_expected"
                      validators={{
                        onSubmit: ({ value }) =>
                          parseRecordList(value).length === 0 ? 'Add at least one expected record' : undefined,
                      }}
                    >
                      {(field) => (
                        <div className="space-y-2">
                          <Label htmlFor="dns_record_expected">Expected records</Label>
                          <Input
                            id="dns_record_expected"
                            placeholder="93.184.215.14, 93.184.215.15"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            disabled={isPending}
                          />
                          {field.state.meta.errors?.[0] && (
                            <p className="text-sm text-destructive">{String(field.state.meta.errors[0])}</p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            Comma-separated. Down when the resolved set differs in any way &mdash; a record added or removed.
                          </p>
                        </div>
                      )}
                    </form.Field>
                  </div>
                )}
                {monitorType === 'sitemap' && (
                    <form.Field name="sitemap_sample">
                      {(field) => (
                        <div className="space-y-2">
                          <Label htmlFor="sitemap_sample">URLs to check per run</Label>
                          <Input
                            id="sitemap_sample"
                            type="number"
                            min="1"
                            max="50"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(Number(e.target.value))}
                            onBlur={field.handleBlur}
                            disabled={isPending}
                          />
                          <p className="text-xs text-muted-foreground">
                            Point the URL at your site or its sitemap (a site root checks <code className="font-mono">/sitemap.xml</code>). Each run requests this many sitemap URLs, spread across the list, and is down if any returns 4xx/5xx.
                          </p>
                        </div>
                      )}
                    </form.Field>
                )}
                {monitorType === 'blocklist' && (
                    <form.Field name="blocklist_zones">
                      {(field) => (
                        <div className="space-y-2">
                          <Label htmlFor="blocklist_zones">Blocklist zones</Label>
                          <Input
                            id="blocklist_zones"
                            placeholder="bl.spamcop.net, b.barracudacentral.org"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            disabled={isPending}
                          />
                          <p className="text-xs text-muted-foreground">
                            Comma-separated DNSBL zones. Leave blank for the defaults (Spamhaus ZEN, SpamCop, Barracuda). Down when the host&rsquo;s IP is listed on any of them.
                          </p>
                        </div>
                      )}
                    </form.Field>
                )}
                </>
              )}
            </form.Subscribe>

            {/* Smart defaults preview — reflects the user's SELECTED type/method,
                not the smart-default's URL-detected guess (a Browser monitor must
                not read "HTTP GET"). Method is only meaningful for http. */}
            {defaults && (
              <form.Subscribe
                selector={(state) => ({
                  interval: state.values.check_interval,
                  type: state.values.monitor_type,
                  method: state.values.http_method,
                  name: state.values.name,
                })}
              >
                {({ interval, type, method, name }) => (
                  <p className="text-xs text-muted-foreground">
                    Will monitor as:{' '}
                    <span className="font-medium text-foreground">{name || defaults.name}</span>
                    {' · '}
                    {type.toUpperCase()}
                    {type === 'http' ? ` ${method}` : ''}
                    {' · '}Every {formatInterval(interval)}
                  </p>
                )}
              </form.Subscribe>
            )}

            {/* Name + the type-specific essential fields (e.g. the Browser
                script, the DNS record) are shown up front — no expander needed.
                Only the cross-cutting options below collapse into "More options". */}
            {/* Name (auto-filled from smart defaults) */}
                <form.Field name="name">
                  {(field) => (
                    <div className="space-y-2">
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        placeholder="Auto-detected from URL"
                        value={field.state.value}
                        onChange={(e) => {
                          field.handleChange(e.target.value)
                          setNameManuallyEdited(true)
                        }}
                        onBlur={field.handleBlur}
                        disabled={isPending}
                      />
                      {field.state.meta.errors?.[0] && (
                        <p className="text-sm text-destructive">
                          {typeof field.state.meta.errors[0] === 'string'
                            ? field.state.meta.errors[0]
                            : getFieldError(field.state.meta.errors[0])}
                        </p>
                      )}
                    </div>
                  )}
                </form.Field>

                {/* HTTP-specific fields */}
                <form.Subscribe selector={(state) => state.values.monitor_type}>
                  {(monitorType) =>
                    monitorType === 'http' || monitorType === 'keyword' ? (
                      <>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <form.Field name="http_method">
                            {(field) => (
                              <div className="space-y-2">
                                <Label>HTTP Method</Label>
                                <Select
                                  value={field.state.value}
                                  onValueChange={(v) => field.handleChange(v as HttpMethod)}
                                  disabled={isPending}
                                >
                                  <SelectTrigger aria-label="HTTP Method">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {HTTP_METHODS.map((m) => (
                                      <SelectItem key={m} value={m}>{m}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </form.Field>

                          {/* Every plan, the free tier included, chooses the interval (owner, 2026-10-19). */}
                          <form.Field name="check_interval">
                            {(field) => (
                              <div className="space-y-2">
                                <Label>Check Interval</Label>
                                <Select
                                  value={String(field.state.value)}
                                  onValueChange={(v) => {
                                    const n = Number(v)
                                    if (n === 30 && fastSlotsExhausted) {
                                      field.handleChange(60)
                                      setAutoSwitchedNotice(
                                        `All ${fastSlotLimit} of your 30-second slots are in use — this monitor will check every 1 minute.`,
                                      )
                                    } else {
                                      field.handleChange(n)
                                      setAutoSwitchedNotice(null)
                                    }
                                  }}
                                  disabled={isPending}
                                >
                                  <SelectTrigger aria-label="Check Interval">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {checkIntervalOptions().map((o) => {
                                      const full = o.value === 30 && fastSlotsExhausted
                                      return (
                                        <SelectItem key={o.value} value={String(o.value)} disabled={full}>
                                          {o.label}{full ? ' (slots full)' : ''}
                                        </SelectItem>
                                      )
                                    })}
                                  </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground">
                                  {freeTier
                                    ? `New free monitors check every ${freePlan.intervalSeconds} seconds. Choose a longer interval for fewer checks.`
                                    : organization?.plan === 'free'
                                      ? `${usedFastSlots}/${fastSlotLimit} 30-second slots used without a plan. Choose a plan for 30-second checks on every monitor.`
                                      : 'Unlimited 30-second monitoring on your plan'}
                                </p>
                                {autoSwitchedNotice && (
                                  <div className="rounded-md border border-border bg-muted px-3 py-2 text-sm">
                                    <p className="text-primary dark:text-primary">{autoSwitchedNotice}</p>
                                  </div>
                                )}
                                {apiError?.field === 'interval' && (
                                  <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm">
                                    <p className="font-medium text-destructive">{apiError.message}</p>
                                    {apiError.hint && (
                                      <p className="text-xs mt-1 text-muted-foreground">{apiError.hint}</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </form.Field>
                        </div>

                        {/* Check location: one fixed location on the free plan */}
                        {freeTier && (
                          <div className="space-y-2">
                            <Label>Check Location</Label>
                            <div className="flex items-center gap-3 rounded-md border border-border px-3 py-2">
                              <div className="h-2 w-2 rounded-full bg-status-up" />
                              <span className="text-sm">{freePlan.locationLabel}</span>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {MONITOR_LOCATIONS.formHint}
                            </p>
                          </div>
                        )}

                        {/* Check Regions */}
                        {!freeTier && !unifiedAreas && regions.length > 0 && (
                          <div className="space-y-2">
                            <Label>Check Regions</Label>
                            <div className="space-y-2">
                              {regions.map((r) => (
                                <label key={r.id} className="flex items-center gap-3 rounded-md border px-3 py-2 cursor-pointer hover:bg-muted/50">
                                  <Checkbox
                                    aria-label={r.name}
                                    checked={selectedRegions.includes(r.id)}
                                    onCheckedChange={(checked) => {
                                      setSelectedRegions((prev) =>
                                        checked ? [...prev, r.id] : prev.filter((id) => id !== r.id)
                                      )
                                    }}
                                    disabled={isPending}
                                  />
                                  <div className="flex items-center gap-2">
                                    <div className="h-2 w-2 rounded-full bg-status-up" />
                                    <span className="text-sm">{r.name}</span>
                                  </div>
                                </label>
                              ))}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Select regions to check from.
                            </p>
                          </div>
                        )}

                        {unifiedAreas && edgeInfo && (
                          <AreaList
                            info={edgeInfo}
                            value={{ regionIds: selectedRegions, edgeAreas }}
                            onChange={(next) => {
                              setSelectedRegions(next.regionIds)
                              setEdgeAreas(next.edgeAreas)
                            }}
                            disabled={isPending}
                            citiesAllowed={citiesAllowed}
                          />
                        )}

                        <form.Field name="request_headers">
                          {(field) => (
                            <div className="space-y-2">
                              <Label>Custom Headers</Label>
                              <textarea
                                aria-label="Custom Headers"
                                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                placeholder={"Authorization: Bearer token123\nContent-Type: application/json"}
                                value={field.state.value}
                                onChange={(e) => field.handleChange(e.target.value)}
                                onBlur={field.handleBlur}
                                disabled={isPending}
                              />
                              <p className="text-xs text-muted-foreground">
                                One header per line in Key: Value format
                              </p>
                            </div>
                          )}
                        </form.Field>

                        <form.Field name="verify_ssl">
                          {(field) => (
                            <div className="space-y-2">
                              <label className="flex items-start gap-3 rounded-md border px-3 py-2 cursor-pointer hover:bg-muted/50">
                                <Checkbox
                                  aria-label="Verify SSL certificate"
                                  checked={field.state.value}
                                  onCheckedChange={(checked) => field.handleChange(checked === true)}
                                  disabled={isPending}
                                />
                                <div>
                                  <span className="text-sm font-medium">Verify SSL certificate</span>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    On by default. The check fails if the certificate chain can't be
                                    validated (e.g. a missing intermediate). Turn off only for
                                    self-signed or internal endpoints you trust.
                                  </p>
                                </div>
                              </label>
                            </div>
                          )}
                        </form.Field>

                        <form.Subscribe selector={(state) => state.values.http_method}>
                          {(method) =>
                            ['POST', 'PUT', 'PATCH'].includes(method) ? (
                              <form.Field name="request_body">
                                {(field) => (
                                  <div className="space-y-2">
                                    <Label>Request Body</Label>
                                    <textarea
                                      aria-label="Request Body"
                                      className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                      placeholder='{"key": "value"}'
                                      value={field.state.value}
                                      onChange={(e) => field.handleChange(e.target.value)}
                                      onBlur={field.handleBlur}
                                      disabled={isPending}
                                    />
                                    {apiError?.field === 'request_body' && (
                                      <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm">
                                        <p className="font-medium text-destructive">{apiError.message}</p>
                                        {apiError.hint && (
                                          <p className="text-xs mt-1 text-muted-foreground">{apiError.hint}</p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </form.Field>
                            ) : null
                          }
                        </form.Subscribe>
                      </>
                    ) : null
                  }
                </form.Subscribe>

                {/* DNS-specific fields */}
                <form.Subscribe selector={(state) => state.values.monitor_type}>
                  {(monitorType) =>
                    monitorType === 'dns' ? (
                      <>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <form.Field name="dns_record_type">
                            {(field) => (
                              <div className="space-y-2">
                                <Label>Record Type</Label>
                                <Select
                                  value={field.state.value}
                                  onValueChange={field.handleChange}
                                  disabled={isPending}
                                >
                                  <SelectTrigger aria-label="Record Type">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {DNS_RECORD_TYPES.map((t) => (
                                      <SelectItem key={t} value={t}>{t}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </form.Field>

                          <form.Field name="check_interval">
                            {(field) => (
                              <div className="space-y-2">
                                <Label>Check Interval</Label>
                                <Select
                                  value={String(field.state.value)}
                                  onValueChange={(v) => field.handleChange(Number(v))}
                                  disabled={isPending}
                                >
                                  <SelectTrigger aria-label="Check Interval">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {checkIntervalOptions(60).map((o) => (
                                      <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </form.Field>
                        </div>

                        <form.Subscribe selector={(state) => ({ recordType: state.values.dns_record_type, expectedValue: state.values.dns_expected_value })}>
                          {({ recordType, expectedValue }) => {
                            const validator = DNS_VALUE_VALIDATORS[recordType]
                            const warning = expectedValue.trim() && validator ? validator(expectedValue.trim()) : null
                            return (
                              <form.Field name="dns_expected_value">
                                {(field) => (
                                  <div className="space-y-2">
                                    <Label>Expected Value</Label>
                                    <Input
                                      aria-label="Expected Value"
                                      placeholder={DNS_PLACEHOLDER_HINTS[recordType] ?? '93.184.216.34'}
                                      value={field.state.value}
                                      onChange={(e) => field.handleChange(e.target.value)}
                                      onBlur={field.handleBlur}
                                      disabled={isPending}
                                    />
                                    {warning ? (
                                      <p className="text-xs text-status-degraded">{warning}</p>
                                    ) : (
                                      <p className="text-xs text-muted-foreground">
                                        Leave empty to just verify the record exists
                                      </p>
                                    )}
                                  </div>
                                )}
                              </form.Field>
                            )
                          }}
                        </form.Subscribe>

                        <form.Field name="dns_server">
                          {(field) => (
                            <div className="space-y-2">
                              <Label>DNS Server</Label>
                              <Input
                                aria-label="DNS Server"
                                placeholder="8.8.8.8 (leave empty for system default)"
                                value={field.state.value}
                                onChange={(e) => field.handleChange(e.target.value)}
                                onBlur={field.handleBlur}
                                disabled={isPending}
                              />
                            </div>
                          )}
                        </form.Field>
                      </>
                    ) : null
                  }
                </form.Subscribe>

                {/* Browser-specific fields */}
                <form.Subscribe selector={(state) => state.values.monitor_type}>
                  {(monitorType) =>
                    monitorType === 'browser' ? (
                      <>
                        <form.Field name="browser_script">
                          {(field) => (
                            <div className="space-y-2">
                              <div className="flex flex-wrap items-center gap-2">
                                <Label className="mr-1">Start from a template:</Label>
                                {BROWSER_SCRIPT_TEMPLATES.map((t) => (
                                  <button
                                    key={t.key}
                                    type="button"
                                    className="rounded border border-input bg-secondary px-2 py-0.5 text-xs hover:bg-accent"
                                    onClick={() => field.handleChange(t.script)}
                                  >
                                    {t.label}
                                  </button>
                                ))}
                                {/* AI authoring — free for any signed-in user (per-org throttled);
                                    inert server-side until an LLM provider is configured. */}
                                <button
                                  type="button"
                                  disabled={aiLoading}
                                  className="ml-auto rounded border border-border bg-muted px-2 py-0.5 text-xs font-medium text-primary hover:bg-accent disabled:opacity-60"
                                  onClick={async () => {
                                    const url = (form.state.values.url ?? '').trim()
                                    if (!url) {
                                      toast.error('Enter the URL above first')
                                      return
                                    }
                                    setAiLoading(true)
                                    try {
                                      const { script } = await apiV2.authorBrowserCheck(url)
                                      field.handleChange(script)
                                      toast.success('Script drafted — review and edit before saving')
                                    } catch (e) {
                                      toast.error(e instanceof Error ? e.message : 'Generation failed')
                                    } finally {
                                      setAiLoading(false)
                                    }
                                  }}
                                >
                                  {aiLoading ? 'Generating…' : '✨ Generate with AI'}
                                </button>
                              </div>
                              <Label>Playwright script</Label>
                              <textarea
                                aria-label="Playwright script"
                                rows={10}
                                className="flex min-h-[200px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                placeholder={"await step('homepage loads', async () => {\n  await page.goto(url)\n  expect(await page.title()).toContain('Home')\n})"}
                                value={field.state.value ?? ''}
                                onChange={(e) => field.handleChange(e.target.value)}
                                onBlur={field.handleBlur}
                                disabled={isPending}
                              />
                              <p className="text-xs text-muted-foreground">
                                Runs in an async function with <code className="font-mono">page</code>, <code className="font-mono">context</code>, <code className="font-mono">expect</code>, <code className="font-mono">step</code>, and <code className="font-mono">url</code> in scope. Wrap actions in <code className="font-mono">step('name', async () =&gt; {'{'}...{'}'})</code> for per-step timing.
                              </p>
                            </div>
                          )}
                        </form.Field>

                        <div className="grid gap-4 sm:grid-cols-2">
                          <form.Field name="browser_engine">
                            {(field) => (
                              <div className="space-y-2">
                                <Label>Engine</Label>
                                <Select
                                  value={field.state.value ?? 'chromium'}
                                  onValueChange={(v) => field.handleChange(v as BrowserEngine)}
                                  disabled={isPending}
                                >
                                  <SelectTrigger aria-label="Engine">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="chromium">Chromium (full browser)</SelectItem>
                                    <SelectItem value="js">Lightweight (JS)</SelectItem>
                                  </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground">
                                  Chromium runs a real browser. Lightweight (JS) is a faster tier with fetch/DOM/assert only — no page interaction.
                                </p>
                              </div>
                            )}
                          </form.Field>

                          <form.Field name="check_interval">
                            {(field) => {
                              // Only offer intervals the org's plan allows — the
                              // backend 422s anything below browser_min_cadence.
                              const floor = browserMinCadence(organization?.plan)
                              const selected = Math.max(field.state.value, floor)
                              return (
                                <div className="space-y-2">
                                  <Label>Check Interval</Label>
                                  <Select
                                    value={String(selected)}
                                    onValueChange={(v) => field.handleChange(Number(v))}
                                    disabled={isPending}
                                  >
                                    <SelectTrigger aria-label="Check Interval">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {BROWSER_INTERVAL_OPTIONS.filter((o) => o.value >= floor).map((o) => (
                                        <SelectItem key={o.value} value={String(o.value)}>
                                          {o.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <p className="text-xs text-muted-foreground">
                                    {floor === 3600
                                      ? 'Your plan runs browser checks hourly. Upgrade for faster checks.'
                                      : floor === 60
                                        ? 'Your plan runs browser checks as often as every minute.'
                                        : `Your plan runs browser checks at most every ${floor / 60} minutes.`}
                                  </p>
                                </div>
                              )
                            }}
                          </form.Field>
                        </div>

                        {/* Visual regression — chromium engine only */}
                        <form.Subscribe selector={(state) => state.values.browser_engine}>
                          {(engine) =>
                            engine === 'chromium' ? (
                              <div className="space-y-3 rounded-md border border-border p-4">
                                <form.Field name="visual_regression">
                                  {(field) => (
                                    <label className="flex items-start gap-3">
                                      <Checkbox
                                        aria-label="Visual regression"
                                        checked={field.state.value ?? false}
                                        onCheckedChange={(c) => field.handleChange(c === true)}
                                        disabled={isPending}
                                      />
                                      <span className="space-y-1">
                                        <span className="block text-sm font-medium">Visual regression</span>
                                        <span className="block text-xs text-muted-foreground">
                                          Compare each run's screenshot to a baseline (auto-set from the first
                                          successful run). A visual change below the match threshold marks the check down.
                                        </span>
                                      </span>
                                    </label>
                                  )}
                                </form.Field>

                                <form.Subscribe selector={(state) => state.values.visual_regression}>
                                  {(on) =>
                                    on ? (
                                      <form.Field name="visual_threshold">
                                        {(field) => (
                                          <div className="space-y-2">
                                            <Label>Match threshold (SSIM)</Label>
                                            <Select
                                              value={String(field.state.value ?? 0.97)}
                                              onValueChange={(v) => field.handleChange(Number(v))}
                                              disabled={isPending}
                                            >
                                              <SelectTrigger aria-label="Match threshold (SSIM)">
                                                <SelectValue />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="0.99">0.99 — strict</SelectItem>
                                                <SelectItem value="0.97">0.97 — recommended</SelectItem>
                                                <SelectItem value="0.95">0.95 — lenient</SelectItem>
                                                <SelectItem value="0.9">0.90 — very lenient</SelectItem>
                                              </SelectContent>
                                            </Select>
                                            <p className="text-xs text-muted-foreground">
                                              Higher = more sensitive to change. 1.0 is identical; below the cutoff fails.
                                            </p>
                                          </div>
                                        )}
                                      </form.Field>
                                    ) : null
                                  }
                                </form.Subscribe>
                              </div>
                            ) : null
                          }
                        </form.Subscribe>
                      </>
                    ) : null
                  }
                </form.Subscribe>

                {/* SSL-specific fields */}
                <form.Subscribe selector={(state) => state.values.monitor_type}>
                  {(monitorType) =>
                    monitorType === 'ssl' ? (
                      <div className="grid gap-4 sm:grid-cols-2">
                        <form.Field name="ssl_expiry_threshold">
                          {(field) => (
                            <div className="space-y-2">
                              <Label>Expiry Alert Threshold</Label>
                              <Select
                                value={String(field.state.value)}
                                onValueChange={(v) => field.handleChange(Number(v))}
                                disabled={isPending}
                              >
                                <SelectTrigger aria-label="Expiry Alert Threshold">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="7">7 days</SelectItem>
                                  <SelectItem value="14">14 days</SelectItem>
                                  <SelectItem value="30">30 days</SelectItem>
                                  <SelectItem value="60">60 days</SelectItem>
                                  <SelectItem value="90">90 days</SelectItem>
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-muted-foreground">
                                Alert when certificate expires within this many days
                              </p>
                            </div>
                          )}
                        </form.Field>

                        <form.Field name="check_interval">
                          {(field) => (
                            <div className="space-y-2">
                              <Label>Check Interval</Label>
                              <Select
                                value={String(field.state.value)}
                                onValueChange={(v) => field.handleChange(Number(v))}
                                disabled={isPending}
                              >
                                <SelectTrigger aria-label="Check Interval">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="3600">1 hour</SelectItem>
                                  <SelectItem value="21600">6 hours</SelectItem>
                                  <SelectItem value="43200">12 hours</SelectItem>
                                  <SelectItem value="86400">24 hours</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </form.Field>
                      </div>
                    ) : null
                  }
                </form.Subscribe>

                {/* Cross-cutting options — collapsed by default to keep the form short. */}
                <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" type="button" className="flex items-center gap-1 px-0 text-muted-foreground hover:text-foreground">
                      <ChevronRight className={`h-4 w-4 transition-transform ${advancedOpen ? 'rotate-90' : ''}`} />
                      More options
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-4 pt-2">
                <form.Field name="timeout">
                  {(field) => (
                    <div className="space-y-2">
                      <Label>Timeout (seconds)</Label>
                      <Input
                        aria-label="Timeout (seconds)"
                        type="number"
                        min="5"
                        max={timeoutMax({ freeTier, editing: false })}
                        value={field.state.value}
                        onChange={(e) => field.handleChange(Number(e.target.value))}
                        onBlur={field.handleBlur}
                        disabled={isPending}
                      />
                      {freeTier && (
                        <p className="text-xs text-muted-foreground">The free plan allows up to 30 seconds.</p>
                      )}
                      {field.state.meta.errors?.[0] && (
                        <p className="text-sm text-destructive">
                          {typeof field.state.meta.errors[0] === 'string'
                            ? field.state.meta.errors[0]
                            : getFieldError(field.state.meta.errors[0])}
                        </p>
                      )}
                    </div>
                  )}
                </form.Field>

                <form.Field name="confirmation_threshold">
                  {(field) => (
                    <div className="space-y-2">
                      <Label>Alert Confirmation</Label>
                      <Select
                        value={String(field.state.value)}
                        onValueChange={(v) => field.handleChange(Number(v))}
                        disabled={isPending}
                      >
                        <SelectTrigger aria-label="Alert Confirmation">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 check (immediate)</SelectItem>
                          <SelectItem value="2">2 consecutive checks</SelectItem>
                          <SelectItem value="3">3 consecutive checks</SelectItem>
                          <SelectItem value="4">4 consecutive checks</SelectItem>
                          <SelectItem value="5">5 consecutive checks</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        How many consecutive failed checks before creating an incident. Higher = fewer false alerts.
                      </p>
                    </div>
                  )}
                </form.Field>

                <form.Field name="reminder_interval_minutes">
                  {(field) => (
                    <div className="space-y-2">
                      <Label>Still-Down Reminders</Label>
                      <Select
                        value={field.state.value === null ? 'off' : String(field.state.value)}
                        onValueChange={(v) =>
                          field.handleChange(v === 'off' ? null : Number(v))
                        }
                        disabled={isPending}
                      >
                        <SelectTrigger aria-label="Still-Down Reminders">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="off">Off</SelectItem>
                          <SelectItem value="15">Every 15 minutes</SelectItem>
                          <SelectItem value="30">Every 30 minutes</SelectItem>
                          <SelectItem value="60">Every hour</SelectItem>
                          <SelectItem value="180">Every 3 hours</SelectItem>
                          <SelectItem value="360">Every 6 hours</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Get reminded via your alert channels while a monitor stays down. Respects quiet hours. Stops when the monitor recovers or the incident is acknowledged, and after 5 reminders or 24 hours.
                      </p>
                    </div>
                  )}
                </form.Field>

                <form.Field name="response_time_threshold">
                  {(field) => (
                    <div className="space-y-2">
                      <Label>Response Time Alert (ms)</Label>
                      <Input
                        aria-label="Response Time Alert (ms)"
                        type="number"
                        min="0"
                        max="30000"
                        placeholder="0 (disabled)"
                        value={field.state.value || ''}
                        onChange={(e) => field.handleChange(Number(e.target.value) || 0)}
                        onBlur={field.handleBlur}
                        disabled={isPending}
                      />
                      <p className="text-xs text-muted-foreground">
                        Create an incident when response time exceeds this threshold. Set to 0 to disable.
                      </p>
                    </div>
                  )}
                </form.Field>

                {/* Response Assertions */}
                <form.Subscribe selector={(state) => state.values.monitor_type}>
                  {(monitorType) =>
                    monitorType === 'http' ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label>Response Assertions</Label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setAssertions([...assertions, { type: 'json_path', target: '', operator: 'eq', value: '' }])
                            }
                            disabled={isPending}
                          >
                            <Plus className="mr-1 h-3 w-3" />
                            Add
                          </Button>
                        </div>
                        {assertions.length === 0 && (
                          <p className="text-xs text-muted-foreground">
                            Validate response body, headers, or status code. All assertions must pass for the check to be "up".
                          </p>
                        )}

                        {!assertions.some(a => a.type === 'status_code') && (
                          <p className="text-xs text-muted-foreground">
                            Default: 2xx = up, anything else = down.
                          </p>
                        )}
                        {assertions.map((assertion, idx) => (
                          <div key={idx} className="flex flex-wrap items-start gap-2 rounded-md border border-border bg-muted/30 p-3">
                            <Select
                              value={assertion.type}
                              onValueChange={(v) => {
                                const next = [...assertions]
                                next[idx] = { ...next[idx], type: v as Assertion['type'] }
                                setAssertions(next)
                              }}
                              disabled={isPending}
                            >
                              <SelectTrigger aria-label={`Assertion ${idx + 1} type`} className="w-[140px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ASSERTION_TYPES.map((t) => (
                                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            {!['contains', 'not_contains', 'status_code'].includes(assertion.type) && (
                              <Input
                                aria-label={`Assertion ${idx + 1} target`}
                                className="w-[160px]"
                                placeholder={
                                  assertion.type === 'json_path' ? '$.data.status'
                                    : assertion.type === 'header' ? 'content-type'
                                    : assertion.type === 'regex' ? 'pattern'
                                    : 'target'
                                }
                                value={assertion.target}
                                onChange={(e) => {
                                  const next = [...assertions]
                                  next[idx] = { ...next[idx], target: e.target.value }
                                  setAssertions(next)
                                }}
                                disabled={isPending}
                              />
                            )}

                            {['json_path', 'header', 'status_code'].includes(assertion.type) && (
                              <Select
                                value={assertion.operator}
                                onValueChange={(v) => {
                                  const next = [...assertions]
                                  next[idx] = { ...next[idx], operator: v }
                                  setAssertions(next)
                                }}
                                disabled={isPending}
                              >
                                <SelectTrigger aria-label={`Assertion ${idx + 1} operator`} className="w-[90px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {OPERATORS.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}

                            {assertion.type !== 'regex' && (
                              <Input
                                aria-label={`Assertion ${idx + 1} expected value`}
                                className="flex-1 min-w-[120px]"
                                placeholder={
                                  assertion.type === 'status_code' ? '200'
                                    : assertion.type === 'contains' || assertion.type === 'not_contains' ? 'keyword'
                                    : 'expected value'
                                }
                                value={assertion.type === 'contains' || assertion.type === 'not_contains' ? assertion.target : assertion.value}
                                onChange={(e) => {
                                  const next = [...assertions]
                                  if (assertion.type === 'contains' || assertion.type === 'not_contains') {
                                    next[idx] = { ...next[idx], target: e.target.value }
                                  } else {
                                    next[idx] = { ...next[idx], value: e.target.value }
                                  }
                                  setAssertions(next)
                                }}
                                disabled={isPending}
                              />
                            )}

                            {assertion.type === 'regex' && (
                              <Input
                                aria-label={`Assertion ${idx + 1} pattern`}
                                className="flex-1 min-w-[120px] font-mono"
                                placeholder='version":\s*"\d+'
                                value={assertion.target}
                                onChange={(e) => {
                                  const next = [...assertions]
                                  next[idx] = { ...next[idx], target: e.target.value }
                                  setAssertions(next)
                                }}
                                disabled={isPending}
                              />
                            )}

                            <Button
                              type="button"
                              variant="ghost"
                              aria-label={`Remove assertion ${idx + 1}`}
                              size="sm"
                              onClick={() => setAssertions(assertions.filter((_, i) => i !== idx))}
                              disabled={isPending}
                            >
                              <Trash2 className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : null
                  }
                </form.Subscribe>

              </CollapsibleContent>
            </Collapsible>

            {apiError && (apiError.field === null || apiError.field === 'regions') && (
              <div role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
                <p className="font-medium text-destructive">{apiError.message}</p>
                {apiError.hint && (
                  <p className="text-xs mt-1 text-muted-foreground">{apiError.hint}</p>
                )}
                {apiError.addEmail && (
                  <Button asChild size="sm" className="mt-2 mr-3">
                    <Link to="/dashboard/settings" search={{ tab: 'profile', billing: undefined, plan: undefined }} hash="emails">
                      Add a work email
                    </Link>
                  </Button>
                )}
                {apiError.upgrade && (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="px-0 h-auto mt-1"
                    onClick={() => showUpgradePrompt(apiError.message)}
                  >
                    View upgrade options →
                  </Button>
                )}
              </div>
            )}
            </fieldset>
            <div className="flex flex-wrap items-center gap-2 pt-4">
              {createBlock ? (
                <BlockedActionButton>Create Monitor</BlockedActionButton>
              ) : (
                <Button type="submit" disabled={isPending}>
                  {isPending ? 'Creating...' : 'Create Monitor'}
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate({ to: '/dashboard/monitors' })}
              >
                Cancel
              </Button>
              <CreateBlockReason />
            </div>
            </>)}
          </form>
          <WorkEmailDialog open={workEmailOpen} onClose={() => setWorkEmailOpen(false)} />
          {isThirdParty && <ThirdPartyCreateFlow presetSlug={presetService} />}
        </CardContent>
      </Card>
    </div>
  )
}
